import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import { useTenant, type MeResponse } from '../../tenant/TenantContext';

/**
 * Auth gate. The rule is "logged in or logged out — never an error screen":
 * a token/renewal failure re-initiates sign-in automatically, which either
 * resolves silently (SSO session still alive) or lands on the login page.
 * A tight-loop guard prevents a redirect storm if sign-in keeps failing.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { setMe, me } = useTenant();
  const { t } = useTranslation();
  const [loopBlocked, setLoopBlocked] = useState(false);
  const token = auth.user?.access_token;

  const reauthenticate = useCallback(() => {
    const now = Date.now();
    const last = Number(sessionStorage.getItem('usc:reauthAt') ?? 0);
    if (now - last < 6000) {
      // two failures in a row → stop auto-redirecting and let the user act
      setLoopBlocked(true);
      return;
    }
    sessionStorage.setItem('usc:reauthAt', String(now));
    sessionStorage.setItem('usc:returnTo', window.location.pathname + window.location.search);
    void auth.signinRedirect();
  }, [auth]);

  // A clean sign-in resets the loop guard.
  useEffect(() => {
    if (auth.isAuthenticated) sessionStorage.removeItem('usc:reauthAt');
  }, [auth.isAuthenticated]);

  // Renewal failures must never surface as an error — recover instead.
  useEffect(() => {
    const offRenewError = auth.events.addSilentRenewError(() => reauthenticate());
    const offExpired = auth.events.addAccessTokenExpired(() => {
      void auth.signinSilent().catch(() => reauthenticate());
    });
    return () => {
      offRenewError();
      offExpired();
    };
  }, [auth.events, auth, reauthenticate]);

  // Not signed in (or an auth error occurred) → re-authenticate.
  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator || loopBlocked) return;
    if (!auth.isAuthenticated || auth.error) reauthenticate();
  }, [auth.isLoading, auth.activeNavigator, auth.isAuthenticated, auth.error, loopBlocked, reauthenticate]);

  const meQuery = useQuery({
    queryKey: ['me', token],
    enabled: Boolean(token),
    queryFn: () => apiFetch<MeResponse>('/api/v1/me', { token }),
  });

  useEffect(() => {
    if (meQuery.data) setMe(meQuery.data);
  }, [meQuery.data, setMe]);

  if (loopBlocked) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="sm">
          <Text c="dimmed">{t('auth.sessionEnded')}</Text>
          <Button
            onClick={() => {
              sessionStorage.removeItem('usc:reauthAt');
              setLoopBlocked(false);
              void auth.signinRedirect();
            }}
          >
            {t('auth.signIn')}
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!auth.isAuthenticated || !me) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  return <>{children}</>;
}
